import { useState, useEffect } from 'react';
import { LS } from '../../utils/storage';
import { useBackClose } from '../../utils/backStack';
import { ProfilePhotoTab } from './ProfilePhotoTab';

export function AccountSettingsModal({ clientData, onClose, onLogout, onPrefsChange }) {
    useBackClose(true, onClose);
    const [tab, setTab] = useState('profile');
    const [curPw, setCurPw] = useState('');
    const [newPw, setNewPw] = useState('');
    const [pwMsg, setPwMsg] = useState('');
    const [newNick, setNewNick] = useState('');
    const [nickMsg, setNickMsg] = useState('');
    const [sessionMsg, setSessionMsg] = useState('');
    const [prefs, setPrefs] = useState({ autocollapsePin: true });
    const [prefsMsg, setPrefsMsg] = useState('');
    const [prefsLoading, setPrefsLoading] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const token = LS.get('bcToken');

    useEffect(() => {
        if (tab === 'session') loadSessions();
        if (tab === 'prefs') loadPrefs();
        // 탭을 바꿀 때만 불러온다
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const loadSessions = async () => {
        if (!token) return;
        setSessionsLoading(true);
        try {
            const res = await fetch('/api/auth/sessions', { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (data.ok) setSessions(data.sessions || []);
        } catch {}
        finally { setSessionsLoading(false); }
    };

    const loadPrefs = async () => {
        if (!token) return;
        setPrefsLoading(true);
        try {
            const res = await fetch('/api/auth/preferences', { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (data.ok) setPrefs({ autocollapsePin: true, ...data.preferences });
        } catch {}
        finally { setPrefsLoading(false); }
    };

    const savePrefs = async (newPrefs) => {
        if (!token) return;
        try {
            const res = await fetch('/api/auth/preferences', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(newPrefs) });
            const data = await res.json();
            if (data.ok) { setPrefs(data.preferences); setPrefsMsg('✅ 저장됨'); setTimeout(() => setPrefsMsg(''), 2000); if (onPrefsChange) onPrefsChange(data.preferences); }
            else setPrefsMsg('❌ 저장 실패');
        } catch { setPrefsMsg('❌ 서버 오류'); }
    };

    const logoutSession = async (jti) => {
        if (!token) return;
        try {
            const res = await fetch(`/api/auth/sessions/${jti}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (data.ok) {
                setSessions(prev => prev.filter(s => s.jti !== jti));
                setSessionMsg('✅ 로그아웃 완료');
            } else { setSessionMsg('❌ 실패'); }
        } catch { setSessionMsg('❌ 서버 오류'); }
    };

    const logoutAll = async () => {
        if (!token) return setSessionMsg('로그인 토큰 없음');
        try {
            const res = await fetch('/api/auth/logout-all', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (data.ok) { setSessionMsg('✅ 모든 세션 로그아웃 완료'); if (onLogout) setTimeout(onLogout, 1000); }
            else setSessionMsg('❌ 실패');
        } catch { setSessionMsg('❌ 서버 오류'); }
    };

    const changePw = async () => {
        if (!curPw || !newPw) return setPwMsg('입력값을 모두 입력하세요.');
        try {
            const res = await fetch('/api/auth/change-password', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ currentPassword: curPw, newPassword: newPw }) });
            const data = await res.json();
            setPwMsg(data.ok ? '✅ 변경 완료!' : `❌ ${data.message}`);
            if (data.ok) { setCurPw(''); setNewPw(''); }
        } catch { setPwMsg('❌ 서버 오류'); }
    };

    const changeNick = async () => {
        if (!newNick.trim()) return setNickMsg('닉네임을 입력하세요.');
        try {
            // 변경 결과는 WebSocket nickname_changed 이벤트로 화면 전체에 반영된다
            const res = await fetch('/api/auth/change-username', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ newUsername: newNick.trim() }) });
            const data = await res.json();
            setNickMsg(data.ok ? '✅ 변경 완료!' : `❌ ${data.message}`);
            if (data.ok) setNewNick('');
        } catch { setNickMsg('❌ 서버 오류'); }
    };

    const formatUA = (ua) => {
        if (!ua) return '알 수 없는 기기';
        if (/iPhone|iPad/.test(ua)) return 'iOS';
        if (/Android/.test(ua)) return 'Android';
        if (/Windows/.test(ua)) return 'Windows';
        if (/Mac/.test(ua)) return 'macOS';
        if (/Linux/.test(ua)) return 'Linux';
        return ua.slice(0, 30);
    };

    const formatDate = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        return `${d.getMonth()+1}/${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2,'0')}`;
    };

    // 예전엔 색·크기를 요소마다 직접 적어(밝은 회색 테두리 등) 다크 모드에서 어색했고,
    // 탭 5개가 좁은 화면에서 "비밀번..." 처럼 잘렸다. 공통 디자인 토큰을 쓰는 클래스로 바꿨다.
    const TABS = [['profile', '프로필'], ['pw', '비밀번호'], ['nick', '닉네임'], ['session', '세션'], ['prefs', '설정']];
    return (
        <div className="confirm-overlay" onClick={onClose}>
            <div className="confirm-box acct-modal" role="dialog" aria-label="계정 설정" onClick={e => e.stopPropagation()}>
                <div className="acct-head">
                    <strong>계정 설정</strong>
                    <button className="nav-icon-btn" onClick={onClose} aria-label="닫기">✕</button>
                </div>
                <div className="acct-tabs" role="tablist">
                    {TABS.map(([key, label]) => (
                        <button key={key} role="tab" aria-selected={tab === key} className={`acct-tab${tab === key ? ' active' : ''}`} onClick={() => setTab(key)}>{label}</button>
                    ))}
                </div>
                {tab === 'profile' && <ProfilePhotoTab clientData={clientData} />}
                {tab === 'pw' && (
                    <div className="acct-form">
                        <input className="acct-input" type="password" placeholder="현재 비밀번호" value={curPw} onChange={e => setCurPw(e.target.value)} autoComplete="current-password" />
                        <input className="acct-input" type="password" placeholder="새 비밀번호 (8자 이상)" value={newPw} onChange={e => setNewPw(e.target.value)} onKeyDown={e => e.key === 'Enter' && changePw()} autoComplete="new-password" />
                        <button className="acct-btn" onClick={changePw}>변경</button>
                        {pwMsg && <div className="acct-msg">{pwMsg}</div>}
                    </div>
                )}
                {tab === 'nick' && (
                    <div className="acct-form">
                        <div className="acct-note">현재 닉네임: <strong>{clientData.username}</strong></div>
                        <input className="acct-input" type="text" placeholder="새 닉네임 (2~10자)" value={newNick} onChange={e => setNewNick(e.target.value)} onKeyDown={e => e.key === 'Enter' && changeNick()} maxLength={10} />
                        <button className="acct-btn" onClick={changeNick}>변경</button>
                        {nickMsg && <div className="acct-msg">{nickMsg}</div>}
                    </div>
                )}
                {tab === 'session' && (
                    <div className="acct-form">
                        {sessionsLoading ? (
                            <div className="acct-note">불러오는 중...</div>
                        ) : sessions.length === 0 ? (
                            <div className="acct-note">세션 정보 없음</div>
                        ) : (
                            <div className="acct-sessions">
                                {sessions.map(s => (
                                    <div key={s.jti} className="acct-session">
                                        <div>
                                            <b>{formatUA(s.user_agent)}</b>
                                            <small>{s.ip} · {formatDate(s.last_active)}</small>
                                        </div>
                                        <button className="acct-btn danger small" onClick={() => logoutSession(s.jti)}>로그아웃</button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <button className="acct-btn danger" onClick={logoutAll}>모든 기기 로그아웃</button>
                        {sessionMsg && <div className="acct-msg">{sessionMsg}</div>}
                    </div>
                )}
                {tab === 'prefs' && (
                    <div className="acct-form">
                        {prefsLoading ? (
                            <div className="acct-note">불러오는 중...</div>
                        ) : (
                            <div className="acct-pref">
                                <label className="nav-switch">
                                    <span>공지 자동 접힘</span>
                                    <input type="checkbox" checked={!!prefs.autocollapsePin} onChange={e => {
                                        const next = { ...prefs, autocollapsePin: e.target.checked };
                                        setPrefs(next);
                                        savePrefs(next);
                                    }} />
                                    <span className="nav-switch-track" aria-hidden="true" />
                                </label>
                                <div className="acct-note">켜면 휴대폰에서 공지가 기본으로 접혀 있습니다. (PC에서는 기본 펼침) 방마다 따로 정하려면 그 방 공지 바의 "처음 볼 때"에서 펼침·접힘을 고르세요.</div>
                                {prefsMsg && <div className="acct-msg">{prefsMsg}</div>}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
